import React, { FunctionComponent, SyntheticEvent, useRef, useEffect, useState, Children, cloneElement, RefObject } from 'react'
import styles from './style.module.scss'
import { Button } from '../Button'
import { isCallable, valueIsNotNullOrUndefined } from '@afterpay/utils'

export interface ShowMoreLessProps {
  limit?: number
  showMoreText?: string
  showLessText?: string
  onClick?: (e: SyntheticEvent) => void
  onChange?: (isShowingMore: boolean) => void
  children: React.ReactNode
  isShowingMore?: boolean
}

export const ShowMoreLess: FunctionComponent<ShowMoreLessProps> = ({
  limit = 5,
  showMoreText = 'Show more',
  showLessText = 'Show less',
  onClick,
  onChange,
  children,
  isShowingMore,
}) => {
  const [internalIsShowMore, setInternalIsShowMore] = useState(false)
  const isShowMore = typeof isShowingMore !== 'undefined' ? isShowingMore : internalIsShowMore
  const childrenArray = Children.toArray(children)
  const shouldShowButton = childrenArray.length > limit
  const limitedChildren = isShowMore ? childrenArray : childrenArray.slice(0, limit)
  const firstNewItemRef = useRef<HTMLElement>(null)

  const handleStateChange = (newState: boolean, event?: SyntheticEvent): void => {
    // Update internal state only if we're uncontrolled
    if (typeof isShowingMore === 'undefined') {
      setInternalIsShowMore(newState)
    }
    if (isCallable(onClick) && valueIsNotNullOrUndefined(event)) {
      onClick(event)
    }
    if (isCallable(onChange)) {
      onChange(newState)
    }
  }

  const handleShowMore = (e: SyntheticEvent): void => {
    handleStateChange(!isShowMore, e)
  }

  // Focus first new child when expanding
  useEffect(() => {
    if (isShowMore && firstNewItemRef.current != null) {
      firstNewItemRef.current.focus()
    }
  }, [isShowMore])

  return (
    <>
      {limitedChildren.map((child, index) => {
        const isFirstNew = isShowMore && index === limit
        // Only apply ref if the child is a valid element that can receive refs
        if (isFirstNew && React.isValidElement(child)) {
          // For DOM elements (strings like 'div')
          if (typeof child.type === 'string') {
            return React.createElement(child.type, {
              ...child.props,
              ref: firstNewItemRef,
              key: child.key ?? index,
            })
          }

          // For React components, we need to be careful with refs
          // Using type assertion to tell TypeScript this element can accept refs
          try {
            return cloneElement(child as React.ReactElement<any, any>, {
              ref: firstNewItemRef as RefObject<any>,
            })
          } catch (e) {
            // Fallback if the component doesn't accept refs
            return child
          }
        }
        return child
      })}
      {shouldShowButton && (
        <div className={styles.actionButtonContainer}>
          <Button kind='Tertiary' padding='Fluid' onClick={handleShowMore} aria-expanded={isShowMore}>
            {isShowMore ? showLessText : showMoreText}
          </Button>
        </div>
      )}
    </>
  )
}
